import { NextPage } from "next";
import { useIntl } from "react-intl";
import Head from "next/head";
import { PageProps } from "@/pages/_app";
import { Layout } from "@/components/layout/Layout";
import { SEO } from "@/interfaces/seo";
import { SEOWithTemplate } from "@/interfaces/seoWithTemplate";
import HeroBlock from "@/components/common/heroBlock/HeroBlock";
import { Section } from "@/components/common/section/Section";
import {
  PointOfInterestList,
  PointOfInterestListProps,
} from "@/components/common/pointOfInterestList/PointOfInterestList";
import { DepartmentDetails } from "@/interfaces/departmentDetails";
import { StaticMap } from "@/components/common/staticMap/StaticMap";
import { CardCarrousel } from "@/components/common/cardCarrousel/CardCarrousel";

import { getNonEmptyString } from "@/utils/getNonEmptyString";
import { VehicleType } from "@/components/common/searchBlock/children/vehicleTypeSelection/VehicleTypeSelection";
import { FAQProps } from "@/utils/formatCategoryPage";

import { Feature, isFeatureEnabled } from "@/utils/isFeatureEnabled";
import ReviewsBlock2 from "@/components/common/reviewsBlockV1/ReviewsBlock";
import ReviewsBlock1 from "@/components/common/reviewsBlock/ReviewsBlock";
import { StructuredReviewProps } from "@/components/common/structuredData/StructuredData.interface";
import { FAQ } from "../../vehicle/categoryPage/faq/FAQ";

const ReviewsBlock = ({
  name,
  agency,
  rentACarReview,
  reviewsConst,
  image,
}: StructuredReviewProps) => {
  if (!isFeatureEnabled(Feature.NEW_REVIEWS_BLOCK)) {
    return (
      <ReviewsBlock1
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  } else {
    return (
      <ReviewsBlock2
        name={name}
        agency={agency}
        rentACarReview={rentACarReview}
        reviewsConst={reviewsConst}
        image={image}
      />
    );
  }
};

export type DomTomPageContent = {
  seo: SEO;
  pointOfInterestList: PointOfInterestListProps | undefined;
  faq?: FAQProps;
  seoWithTemplate: SEOWithTemplate;
};
type DomTomPageProps = PageProps &
  DomTomPageContent & {
    slug: string;
    department: DepartmentDetails;
    defaultCanonicalRoute: string;
  };

const hostUrl = process.env.NEXT_PUBLIC_HOST_URL ?? "";

export const DomTomPage: NextPage<DomTomPageProps> = ({
  layout,
  reviewsConst,
  department,
  slug,
  pointOfInterestList,
  faq,
  seo,
  seoWithTemplate,
  defaultCanonicalRoute,
}) => {
  const intl = useIntl();

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: intl.formatMessage({ id: "home" }),
                  item: hostUrl,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: intl.formatMessage({ id: "agenciesTitle" }),
                  item: `${hostUrl}/agences`,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: intl.formatMessage(
                    { id: "departmentPageBreadcrumbTitle" },
                    {
                      department: department.name,
                    }
                  ),
                  item: `${hostUrl}/agences/departement/${slug}/`,
                },
              ],
            }),
          }}
        />
      </Head>
      <Layout
        layout={layout}
        pageMeta={seo}
        title={
          seoWithTemplate.overrideTitle ??
          intl.formatMessage(
            { id: "defaultMetaTitle" },
            {
              name: getNonEmptyString(
                seoWithTemplate.valueInTemplateTitle,
                department.name
              ),
            }
          )
        }
        description={
          seoWithTemplate.overrideDescription ??
          intl.formatMessage(
            { id: "defaultMetaDescription" },
            {
              name: getNonEmptyString(
                seoWithTemplate.valueInTemplateDescription,
                department.name
              ),
              additional: department.slug,
            }
          )
        }
        breadcrumbs={[
          { title: intl.formatMessage({ id: "home" }), url: hostUrl },
          {
            title: intl.formatMessage({ id: "agenciesBreadcrumb" }),
            url: `${hostUrl}/agences`,
          },
          {
            title: department.name,
            url: `${hostUrl}/agences/departement/${slug}`,
          },
        ]}
        defaultCanonicalRoute={defaultCanonicalRoute}
      >
        <HeroBlock
          title={intl.formatMessage(
            { id: "departmentPageTitle" },
            {
              department: department.name,
              br: () => <br />,
            }
          )}
          searchBlockInitialState={{
            vehicleType: VehicleType.CAR,
            isOneWayTrip: false,
          }}
        />

        <Section>
          <StaticMap
            mapName={"toutes-les-agences-rent-a-car-v2_1118492"}
            forceX={department.latitude}
            forceY={department.longitude}
            forceZoom={"11"}
            folder={"states"}
          />
        </Section>

        <Section>
          {pointOfInterestList &&
            pointOfInterestList.pointOfInterests.length > 0 && (
              <PointOfInterestList
                location={department.name}
                pointOfInterestList={pointOfInterestList}
              />
            )}

          {department.agencies.length > 0 && (
            <CardCarrousel
              title={intl.formatMessage(
                { id: "agenciesOf" },
                { name: department.name }
              )}
              urlPrefix={`${hostUrl}/agences/`}
              cards={department.agencies}
            />
          )}
        </Section>

        {faq && <FAQ {...faq} />}

        {department.reviews?.clientReviews &&
          department.reviews.clientReviews.length && (
            <ReviewsBlock
              name={department.name}
              rentACarReview={department.reviews}
              reviewsConst={reviewsConst}
            />
          )}
      </Layout>
    </>
  );
};
